import loadable from "@loadable/component";
import { Container, Grid, ThemeProvider } from "@material-ui/core";
import axios from "axios";
import { graphql } from "gatsby";
import React, { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IAtlasDatumRequest } from "src/atlas/AtlasModels";
import { IAppState } from "src/redux/store";
import { loadData } from "../atlas/AtlasActionFactory";
const AtlasHtml = loadable(() => import("../atlas/blades/AtlasHtml"));
const AtlasImage = loadable(() => import("../atlas/blades/AtlasImage"));
const AtlasInfo = loadable(() => import("../atlas/blades/AtlasInfo"));
const AtlasCarousel = loadable(() => import("../atlas/blades/AtlasCarousel"));
const AtlasCellInfo = loadable(() => import("../atlas/blades/AtlasCellInfo"));
const AtlasCopy = loadable(() => import("../atlas/blades/AtlasCopy"));
const AtlasDataset = loadable(() => import("../atlas/blades/AtlasDataset"));
const AtlasDatasets = loadable(() => import("../atlas/blades/AtlasDatasets"));
const AtlasDownloads = loadable(() => import("../atlas/blades/AtlasDownloads"));
const AtlasFooter = loadable(() => import("../atlas/blades/AtlasFooter"));
const AtlasHeatmap = loadable(() => import("../atlas/blades/AtlasHeatmap"));
const AtlasHeatmapAdvanced = loadable(
  () => import("../atlas/blades/AtlasHeatmapAdvanced")
);
const AtlasReferences = loadable(
  () => import("../atlas/blades/AtlasReferences")
);
const AtlasGeneInfo = loadable(() => import("../atlas/blades/AtlasGeneInfo"));
const AtlasHeader = loadable(() => import("../atlas/blades/AtlasHeader"));
const AtlasHistogram = loadable(() => import("../atlas/blades/AtlasHistogram"));
const AtlasIGV = loadable(() => import("../atlas/blades/AtlasIGV"));
const AtlasCorrelationMatrix = loadable(
  () => import("../atlas/blades/AtlasCorrelationMatrix")
);
const AtlasKeyValue = loadable(() => import("../atlas/blades/AtlasKeyValue"));
const AtlasLayout = loadable(() => import("../atlas/blades/AtlasLayout"));
const AtlasLine = loadable(() => import("../atlas/blades/AtlasLine"));
const AtlasLinks = loadable(() => import("../atlas/blades/AtlasLinks"));
const AtlasMarquee = loadable(() => import("../atlas/blades/AtlasMarquee"));
const AtlasMotifInfo = loadable(() => import("../atlas/blades/AtlasMotifInfo"));
const AtlasPercentBar = loadable(
  () => import("../atlas/blades/AtlasPercentBar")
);
const AtlasPercentList = loadable(
  () => import("../atlas/blades/AtlasPercentList")
);
const AtlasPie = loadable(() => import("../atlas/blades/AtlasPie"));
const AtlasProtein = loadable(() => import("../atlas/blades/AtlasProtein"));
const AtlasScatter2d = loadable(() => import("../atlas/blades/AtlasScatter2d"));
const AtlasSearch = loadable(() => import("../atlas/blades/AtlasSearch"));
const AtlasStackedBar = loadable(
  () => import("../atlas/blades/AtlasStackedBar")
);
const AtlasTopTracks = loadable(() => import("../atlas/blades/AtlasTopTracks"));
const AtlasTable = loadable(() => import("../atlas/blades/AtlasTable"));
const AtlasTissueInfo = loadable(
  () => import("../atlas/blades/AtlasTissueInfo")
);
const AtlasVideo = loadable(() => import("../atlas/blades/AtlasVideo"));
const AtlasBoxWhiskers = loadable(
  () => import("../atlas/blades/AtlasBoxWhiskers")
);

// import igv from 'igv.esm.min.js';
const onAppError = (error: Error, componentStack: string) => {
  const subject = "Application Error";
  const message =
    "URL: " +
    window.location.href +
    "\n" +
    error.toString() +
    "\n" +
    componentStack;
  axios({
    method: "post",
    url: "https://viz.fredhutch.org/dev/emailviz",
    data: { subject, message },
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};
const ErrorComponent = ({ componentStack, error }) => (
  <div style={{ padding: "50px" }}>
    <p>
      <strong>An error occured.. </strong>
    </p>
    <p>
      We have notified our technical team and will work to resolve it quickly…
    </p>
    <p>
      <a href="/">Return To Atlas</a>
    </p>
  </div>
);

export const AtlasPageTemplate: React.FC<any> = ({
  components,
  models,
  transforms,
  footer,
}) => {
  // Hooks
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(loadData(models, transforms));
  }, []);

  // Component State
  const data = useSelector((state: IAppState) => {
    return state.atlasState ? state.atlasState.data : {};
  });

  // Sub-Components
  const elComponents: any = [];
  const renderLoader = () => <p>Loading</p>;
  const addComponent = (component: any): void => {
    elComponents.push(component);
  };

  for (const [index, value] of components.entries()) {
    switch (value.component) {
      case "atlas-info":
        addComponent(
          <AtlasInfo data={data} props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-html":
        addComponent(<AtlasHtml props={JSON.parse(value.props)} key={index} />);
        break;
      case "atlas-references":
        addComponent(
          <AtlasReferences
            data={data}
            props={JSON.parse(value.props)}
            key={index}
          />
        );
        break;
      case "atlas-isb-coor":
        addComponent(
          <AtlasImage props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-correlation-matrix":
        addComponent(
          <AtlasCorrelationMatrix props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-carousel":
        addComponent(
          <AtlasCarousel props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-line":
        addComponent(
          <AtlasLine data={data} props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-histogram":
        elComponents.push(
          <AtlasHistogram
            data={data}
            props={JSON.parse(value.props)}
            key={index}
          />
        );
        break;
      case "atlas-header":
        addComponent(
          <AtlasHeader props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-downloads":
        addComponent(
          <AtlasDownloads props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-search":
        addComponent(
          <AtlasSearch
            props={JSON.parse(value.props)}
            data={data}
            key={index}
          />
        );
        break;
      case "atlas-motif-info":
        addComponent(
          <AtlasMotifInfo
            data={data}
            props={JSON.parse(value.props)}
            key={index}
          />
        );
        break;
      case "atlas-gene-info":
        addComponent(
          <AtlasGeneInfo
            data={data}
            props={JSON.parse(value.props)}
            key={index}
          />
        );
        break;
      case "atlas-cell-info":
        addComponent(<AtlasCellInfo key={index} />);
        break;
      case "atlas-tissue-info":
        addComponent(<AtlasTissueInfo key={index} />);
        break;
      // case "atlas-genome-arcs":
      //   addComponent(
      //     <AtlasGenomeArcs
      //       data={data}
      //       props={JSON.parse(value.props)}
      //       key={index}
      //     />
      //   );
      //   break;
      // case "atlas-pubmed":
      //   addComponent(
      //     <AtlasPubmed genes={genes} gene={value.gene} key={index} />
      //   );
      //   break;
      // case "atlas-pathways":
      //   addComponent(
      //     <AtlasPathways genes={genes} gene={value.gene} key={index} />
      //   );
      //   break;
      // case "atlas-go":
      //   addComponent(
      //     <AtlasGo
      //       genes={genes}
      //       gene={value.gene}
      //       key={index}
      //       title={value.title}
      //     />
      //   );
      //   break;
      case "atlas-percent-list":
        addComponent(
          <AtlasPercentList
            props={JSON.parse(value.props)}
            data={data}
            key={index}
          />
        );
        break;
      case "atlas-percent-pie":
        // addComponent(
        //   <AtlasPercentPie
        //     key={index}
        //     csvs={csvs}
        //     csv={value.csv}
        //     title={value.title}
        //   />
        // );
        break;
      case "atlas-percent-bar":
        addComponent(
          <AtlasPercentBar
            props={JSON.parse(value.props)}
            data={data}
            key={index}
          />
        );
        break;
      case "atlas-key-value":
        addComponent(
          <AtlasKeyValue
            data={data}
            props={JSON.parse(value.props)}
            key={index}
          />
        );
        break;
      case "atlas-stacked-bar":
        addComponent(
          <AtlasStackedBar
            data={data}
            props={JSON.parse(value.props)}
            key={index}
          />
        );
        break;
      case "atlas-scatter-2d":
        addComponent(
          <AtlasScatter2d
            key={index}
            props={JSON.parse(value.props)}
            data={data}
          />
        );
        break;
      case "atlas-heatmap":
        addComponent(
          <AtlasHeatmap
            key={index}
            props={JSON.parse(value.props)}
            data={data}
          />
        );
        break;
      case "atlas-table":
        addComponent(
          <AtlasTable data={data} props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-copy":
        addComponent(<AtlasCopy props={JSON.parse(value.props)} key={index} />);
        break;
      case "atlas-dataset":
        addComponent(<AtlasDataset props={value.props} key={index} />);
        break;
      case "atlas-links":
        addComponent(
          <AtlasLinks props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-datasets":
        addComponent(
          <AtlasDatasets props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-igv":
        addComponent(
          <AtlasIGV props={JSON.parse(value.props)} data={data} key={index} />
        );
        break;
      case "atlas-pie":
        addComponent(
          <AtlasPie key={index} props={JSON.parse(value.props)} data={data} />
        );
        break;
      // case "atlas-protein":
      //   addComponent(
      //     <AtlasProtein
      //       key={index}
      //       props={JSON.parse(value.props)}
      //       data={data}
      //     />
      //   );
      //   break;
      case "atlas-marquee":
        addComponent(
          <AtlasMarquee
            key={index}
            props={JSON.parse(value.props)}
            data={data}
          />
        );
        break;
      case "atlas-heatmap-advanced":
        addComponent(
          <AtlasHeatmapAdvanced
            key={index}
            props={JSON.parse(value.props)}
            data={data}
          />
        );
        break;
      case "atlas-histogram":
        addComponent(
          <AtlasHistogram
            key={index}
            props={JSON.parse(value.props)}
            data={data}
          />
        );
      case "atlas-top-tracks":
        addComponent(
          <AtlasTopTracks
            data={data}
            props={JSON.parse(value.props)}
            key={index}
          />
        );
        break;
      case "atlas-box-whiskers":
        addComponent(
          <AtlasBoxWhiskers
            data={data}
            props={JSON.parse(value.props)}
            key={index}
          />
        );
        break;
      case "atlas-video":
        addComponent(
          <AtlasVideo props={JSON.parse(value.props)} key={index} />
        );
        break;
      case "atlas-enhancers":
        break;
      case "atlas-download":
        break;
    }
  }
  return (
    <Fragment>
      <Container fixed style={{ paddingBottom: "60px" }}>
        <Grid container spacing={4}>
          {elComponents}
        </Grid>
      </Container>
      <AtlasFooter props={JSON.parse(footer)} />
    </Fragment>
  );
};

const AtlasPage = (props: any) => {
  // Rework this to pull params from {} and also redirect if fail
  let href = "";
  try {
    href = props.location.href || "";
    if (href.endsWith("/")) {
      href = href.slice(0, -1);
    }
  } finally {
  }
  const path: Array<string> = (href || "url/path").split("/");
  const { frontmatter } = props.data.page;
  let models = [];
  let transforms = [];
  frontmatter.components.forEach((c: any) => {
    if (c.props !== null) {
      c.props = c.props.replace(/{[0-9]+}/gi, (m: string) => {
        const i = parseInt(m.replace("{", "").replace("}", ""), 10);
        try {
          return path[path.length - (1 + i)].replace(/_/g, " ");
        } catch (e) {
          return "build";
        }
      });
    }
  });

  if (frontmatter.transforms !== null) {
    frontmatter.transforms.forEach((t: any) => {
      if (t.steps !== null) {
        t.steps = t.steps.replace(/{[0-9]+}/gi, (m: string) => {
          const i = parseInt(m.replace("{", "").replace("}", ""), 10);
          try {
            return path[path.length - (1 + i)].replace(/_/g, " ");
          } catch (e) {
            return "build";
          }
        });
      }
    });
  }

  if (frontmatter.models !== null) {
    frontmatter.models.forEach((model: IAtlasDatumRequest) => {
      model.url = model.url.replace(/{[0-9]+}/gi, (m: string) => {
        const i = parseInt(m.replace("{", "").replace("}", ""), 10);
        try {
          return path[path.length - (1 + i)]; //.replace(/_/g, " ");
        } catch (e) {
          return "build";
        }
      });
    });
    models = frontmatter.models;
  }

  if (frontmatter.transforms !== null) {
    transforms = frontmatter.transforms.map((v) => ({
      outputs: JSON.parse(v.outputs),
      steps: JSON.parse(v.steps),
    }));
  }

  frontmatter.title = frontmatter.title
    .replace(/{[0-9]+}/gi, (m: string) => {
      const i = parseInt(m.replace("{", "").replace("}", ""), 10);
      return path[path.length - (1 + i)];
    })
    .toUpperCase();

  frontmatter.description = frontmatter.description.replace(
    /{[0-9]+}/gi,
    (m: string) => {
      const i = parseInt(m.replace("{", "").replace("}", ""), 10);
      return path[path.length - (1 + i)];
    }
  );

  frontmatter.keywords = frontmatter.keywords.replace(
    /{[0-9]+}/gi,
    (m: string) => {
      const i = parseInt(m.replace("{", "").replace("}", ""), 10);
      return path[path.length - (1 + i)];
    }
  );

  return (
    // <ErrorBoundary onError={onAppError} FallbackComponent={ErrorComponent}>

    <AtlasLayout config={frontmatter}>
      <AtlasPageTemplate
        title={frontmatter.title}
        footer={frontmatter.footer}
        components={frontmatter.components}
        models={models}
        transforms={transforms}
      ></AtlasPageTemplate>
    </AtlasLayout>

    // </ErrorBoundary>
  );
};

export default AtlasPage;

export const pageQuery = graphql`
  query AtlasPage($id: String, $cid: String) {
    config: markdownRemark(id: { eq: $cid }) {
      frontmatter {
        title
      }
    }
    page: markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        description
        keywords
        background
        icon
        footer
        models {
          name
          type
          url
        }
        transforms {
          outputs
          steps
        }
        components {
          component
          props
        }
      }
    }
  }
`;
